/**
 * @file: screens.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * The settings and account pages. They have no navigator of their own: the
 * Me stack hosts them, so settings and help open as sub-pages of 我的 and
 * back returns there.
 */

import { ErpFlow } from '@euler/app/flows/erp/ErpFlow';
import { NotificationSettingsScreen } from '@euler/app/flows/inbox/pages/settings';
import { ProductDetailPage } from '@euler/app/flows/products/ProductDetailPage';
import { AboutScreen } from '@euler/app/flows/settings/pages/AboutScreen';
import { AccountSecurityScreen } from '@euler/app/flows/settings/pages/AccountSecurityScreen';
import { AccountTransactionsScreen } from '@euler/app/flows/settings/pages/AccountTransactionsScreen';
import { ChangePasswordScreen } from '@euler/app/flows/settings/pages/ChangePasswordScreen';
import { VerifyCodeTipsScreen } from '@euler/app/flows/settings/pages/CodeTipsScreen';
import { DebugPanel } from '@euler/app/flows/settings/pages/DebugPanel';
import { GatewayDeviceScreen } from '@euler/app/flows/settings/pages/GatewayDeviceScreen';
import { GeneralSettingsScreen } from '@euler/app/flows/settings/pages/GeneralSettingsScreen';
import { HelpVideosScreen } from '@euler/app/flows/settings/pages/HelpVideosScreen';
import { NewPasswordScreen } from '@euler/app/flows/settings/pages/NewPasswordScreen';
import { ObdDeviceScreen } from '@euler/app/flows/settings/pages/ObdDeviceScreen';
import { PhoneScreen } from '@euler/app/flows/settings/pages/PhoneScreen';
import { ProductReferralHistoryScreen } from '@euler/app/flows/settings/pages/ProductReferralHistoryPage';
import { ReleaseNotesScreen } from '@euler/app/flows/settings/pages/ReleaseNotes';
import { SettingsScreen } from '@euler/app/flows/settings/pages/SettingsScreen';
import { SwitchAccountScreen } from '@euler/app/flows/settings/pages/SwitchAccountScreen';
import { UserProfileScreen } from '@euler/app/flows/settings/pages/UserProfileScreen';
import { VerifyCodeScreen } from '@euler/app/flows/settings/pages/VerifyCodeScreen';
import { WithdrawResultScreen } from '@euler/app/flows/settings/pages/WithdrawResultScreen';
import { WithdrawScreen } from '@euler/app/flows/settings/pages/WithdrawScreen';
import { WithdrawWeixinAuthScreen } from '@euler/app/flows/settings/pages/WithdrawWeixinAuthScreen';
import { ScreenToParams } from '@euler/functions/spreadScreens';

export const SettingsScreens = {
  // Bare (un-prefixed) names are public: `linkingFactory` drops `_`-prefixed
  // routes when building the URL, so only these can carry a path of their own.
  settings: SettingsScreen,
  // The ERP flow is a stack of its own; `/settings/erp-integration`.
  erpIntegration: ErpFlow,
  _obdDevices: ObdDeviceScreen,
  _gatewayDevices: GatewayDeviceScreen,
  _profile: UserProfileScreen,
  _accountSecurity: AccountSecurityScreen,
  _phone: PhoneScreen,
  _verifyCode: VerifyCodeScreen,
  _codeTips: VerifyCodeTipsScreen,
  _changePassword: ChangePasswordScreen,
  _newPassword: NewPasswordScreen,
  _generalSettings: GeneralSettingsScreen,
  _notificationSettings: NotificationSettingsScreen,
  // The help center; `/help`.
  help: HelpVideosScreen,
  _aboutUs: AboutScreen,
  _debugPanel: DebugPanel,
  _releaseNotes: ReleaseNotesScreen,
  _switchAccount: SwitchAccountScreen,
  _transactions: AccountTransactionsScreen,
  _withdraw: WithdrawScreen,
  _withdrawWeixinAuth: WithdrawWeixinAuthScreen,
  _withdrawResult: WithdrawResultScreen,
  _productReferralHistory: ProductReferralHistoryScreen,
  _productDetail: ProductDetailPage,
};

export type SettingsNavParams = ScreenToParams<typeof SettingsScreens>;
